"use client";

import { useCallback, useSyncExternalStore } from "react";
import { syncProjectCacheInventory } from "@/lib/aicheck/cache-inventory";
import { DISMISSED_PREFIX } from "@/lib/aicheck/cache-keys";

export { DISMISSED_PREFIX } from "@/lib/aicheck/cache-keys";

// ── Shared in-memory store ──────────────────────────────────
// All hook instances share this state so dismiss/restore in any
// component immediately propagates to every other consumer.

const cache = new Map<number, Set<string>>();
const listeners = new Set<() => void>();

function notify() {
  for (const fn of listeners) fn();
}

function subscribe(cb: () => void) {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

function getOrLoad(projectId: number): Set<string> {
  const existing = cache.get(projectId);
  if (existing) return existing;
  try {
    const raw = localStorage.getItem(`${DISMISSED_PREFIX}${projectId}`);
    const set = raw ? new Set(JSON.parse(raw) as string[]) : new Set<string>();
    cache.set(projectId, set);
    return set;
  } catch {
    const set = new Set<string>();
    cache.set(projectId, set);
    return set;
  }
}

function persist(projectId: number, ids: Set<string>) {
  cache.set(projectId, ids);
  localStorage.setItem(
    `${DISMISSED_PREFIX}${projectId}`,
    JSON.stringify([...ids]),
  );
  syncProjectCacheInventory();
  notify();
}

// ── Imperative cache clear (for use outside the hook) ──────
export function clearDismissedCache(projectId: number) {
  cache.delete(projectId);
  try {
    localStorage.removeItem(`${DISMISSED_PREFIX}${projectId}`);
  } catch {
    // ignore
  }
  syncProjectCacheInventory();
  notify();
}

export function clearAllDismissedCaches() {
  cache.clear();
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(DISMISSED_PREFIX)) keys.push(key);
    }
    for (const key of keys) {
      localStorage.removeItem(key);
    }
  } catch {
    // ignore
  }
  syncProjectCacheInventory();
  notify();
}

// ── Hook ────────────────────────────────────────────────────

export function useDismissedChecks(projectId: number) {
  const dismissed = useSyncExternalStore(
    subscribe,
    () => getOrLoad(projectId),
    () => getOrLoad(projectId),
  );

  const dismiss = useCallback(
    (checkId: string) => {
      const next = new Set(getOrLoad(projectId));
      next.add(checkId);
      persist(projectId, next);
    },
    [projectId],
  );

  const restore = useCallback(
    (checkId: string) => {
      const next = new Set(getOrLoad(projectId));
      next.delete(checkId);
      persist(projectId, next);
    },
    [projectId],
  );

  const isDismissed = useCallback(
    (checkId: string) => dismissed.has(checkId),
    [dismissed],
  );

  const dismissedCount = dismissed.size;

  const dismissedIds = useCallback(() => [...dismissed], [dismissed]);

  return { dismiss, restore, isDismissed, dismissedCount, dismissedIds };
}
